import { useState, useRef, useEffect } from 'react';
import { Save, Bold, Italic, Underline, List, ListOrdered, Link } from 'lucide-react';
import Button from '../ui/Button';
import Input from '../ui/Input';

export default function TemplateForm({ template, onSave, onCancel }) {
  const [formData, setFormData] = useState({
    templateId: template?.templateId || '',
    templateName: template?.templateName || '',
    subject: template?.subject || '',
    headerText: template?.headerText || 'Welcome to CertifiAble! 🤟',
    bodyHtml: template?.bodyHtml || '<p>Start typing your email...</p>',
    bodyText: template?.bodyText || '',
    category: template?.category || 'general',
    isActive: template?.isActive !== undefined ? template?.isActive : true
  });
  
  const editorRef = useRef(null);

  // Set initial content once
  useEffect(() => {
    if (editorRef.current && template?.bodyHtml) {
      const match = template.bodyHtml.match(/<div style="background: white[^>]*>(.*?)<\/div>\s*<div style="background: #f9fafb/s);
      const content = match && match[1] ? match[1].trim() : template.bodyHtml;
      editorRef.current.innerHTML = content;
    }
  }, [template]);

  // Auto-generate template ID from name
  const generateTemplateId = (name) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');
  };

  const handleNameChange = (name) => {
    setFormData({
      ...formData,
      templateName: name,
      templateId: template ? formData.templateId : generateTemplateId(name)
    });
  };

  const updateContent = () => {
    if (editorRef.current) {
      const html = editorRef.current.innerHTML;
      const fullHtml = wrapWithTemplate(html);
      const plainText = editorRef.current.innerText;
      
      setFormData(prev => ({
        ...prev,
        bodyHtml: fullHtml,
        bodyText: plainText
      }));
    }
  };

  const updatePreview = () => {
    if (editorRef.current) {
      const html = editorRef.current.innerHTML;
      const fullHtml = wrapWithTemplate(html);
      
      setFormData(prev => ({
        ...prev,
        bodyHtml: fullHtml
      }));
    }
  };

  const wrapWithTemplate = (bodyContent) => {
    return `
<div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
  <div style="background: linear-gradient(135deg, #3B82F6 0%, #8B5CF6 100%); padding: 40px 20px; text-align: center; border-radius: 8px 8px 0 0;">
    <h1 style="color: white; margin: 0; font-size: 28px;">${formData.headerText}</h1>
  </div>
  <div style="background: white; padding: 40px 30px; border: 1px solid #e5e7eb; border-top: none;">
    <style>
      ul { list-style-type: disc; margin-left: 20px; padding-left: 20px; }
      ol { list-style-type: decimal; margin-left: 20px; padding-left: 20px; }
      li { display: list-item; margin-bottom: 8px; }
      a { color: #3B82F6; text-decoration: underline; }
    </style>
    ${bodyContent}
  </div>
  <div style="background: #f9fafb; padding: 20px; text-align: center; border-top: 1px solid #e5e7eb; border-radius: 0 0 8px 8px;">
    <p style="font-size: 12px; color: #9ca3af; margin: 0;">CertifiAble - Because you ARE able! 🤟</p>
    <p style="font-size: 12px; color: #9ca3af; margin: 5px 0 0 0;">
      <a href="https://getcertifiable.com" style="color: #3B82F6; text-decoration: none;">getcertifiable.com</a>
    </p>
  </div>
</div>`;
  };

  const execCommand = (command, value = null) => {
    editorRef.current?.focus();
    const success = document.execCommand(command, false, value);
    console.log(`execCommand ${command}:`, success);
    setTimeout(updatePreview, 10);
  };

  const insertList = (ordered = false) => {
    const command = ordered ? 'insertOrderedList' : 'insertUnorderedList';
    editorRef.current?.focus();
    const success = document.execCommand(command, false, null);
    console.log(`List command ${command}:`, success);
    setTimeout(updatePreview, 10);
  };

  const insertLink = () => {
    editorRef.current?.focus();
    const selection = window.getSelection();
    const selectedText = selection.toString();
    
    const url = prompt('Enter URL:', 'https://');
    if (!url) return;
    
    if (selectedText) {
      // If text is selected, make it a link
      document.execCommand('createLink', false, url);
    } else {
      // If no text selected, insert link with URL as text
      const linkHtml = `<a href="${url}" style="color: #3B82F6; text-decoration: underline;">${url}</a>`;
      document.execCommand('insertHTML', false, linkHtml);
    }
    setTimeout(updatePreview, 10);
  };

  const insertButton = () => {
    const url = prompt('Enter button URL:', 'https://certifiable.me/dashboard');
    const text = prompt('Enter button text:', 'Go to Dashboard');
    if (url && text) {
      const buttonHtml = `<a href="${url}" style="background: linear-gradient(135deg, #3B82F6 0%, #8B5CF6 100%); color: white; padding: 12px 24px; text-decoration: none; border-radius: 6px; font-weight: 600; display: inline-block; margin: 10px 0;">${text}</a>`;
      document.execCommand('insertHTML', false, buttonHtml);
      setTimeout(updatePreview, 0);
    }
  };

  const insertVariable = (variable) => {
    document.execCommand('insertHTML', false, `{{${variable}}}`);
    editorRef.current?.focus();
    setTimeout(updatePreview, 0);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Capture final content from editor
    if (editorRef.current) {
      const html = editorRef.current.innerHTML;
      const fullHtml = wrapWithTemplate(html);
      const plainText = editorRef.current.innerText;
      
      const finalData = {
        ...formData,
        bodyHtml: fullHtml,
        bodyText: plainText
      };
      
      onSave(finalData);
    } else {
      onSave(formData);
    }
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h3 className="text-xl font-semibold text-gray-900">
          {template ? 'Edit Email Template' : 'New Email Template'}
        </h3>
        <div className="flex gap-3">
          <Button type="button" variant="outline" size="sm" onClick={onCancel}>Cancel</Button>
          <Button type="submit" size="sm" onClick={handleSubmit}>
            <Save className="w-4 h-4 mr-2" />
            {template ? 'Update' : 'Create'}
          </Button>
        </div>
      </div>

      {/* Help Tips */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
        <div className="flex justify-between items-center">
          <div>
            <h4 className="font-semibold text-gray-900 text-sm mb-1">Insert Variables:</h4>
            <div className="flex gap-2 flex-wrap">
              {['firstName', 'lastName', 'email', 'certifyingBody', 'cycleEndDate', 'totalCEUs'].map(v => (
                <button
                  key={v}
                  type="button"
                  onClick={() => insertVariable(v)}
                  className="text-xs bg-white px-2 py-1 rounded border border-blue-300 hover:bg-blue-100"
                >
                  {'{{'}{v}{'}}'}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Template Details */}
      <div className="grid grid-cols-3 gap-4">
        <Input
          label="Template Name"
          value={formData.templateName}
          onChange={(e) => handleNameChange(e.target.value)}
          placeholder="Welcome Email"
          required
        />
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
          <select
            value={formData.category}
            onChange={(e) => setFormData({...formData, category: e.target.value})}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="general">General</option>
            <option value="reminder">Reminder</option>
            <option value="invitation">Invitation</option>
            <option value="announcement">Announcement</option>
            <option value="notification">Notification</option>
            <option value="billing">Billing</option>
          </select>
        </div>
        <div className="flex items-end">
          <label className="flex items-center gap-2 pb-2">
            <input
              type="checkbox"
              checked={formData.isActive}
              onChange={(e) => setFormData({...formData, isActive: e.target.checked})}
              className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
            />
            <span className="text-sm font-medium text-gray-700">Active</span>
          </label>
        </div>
      </div>

      {/* Subject */}
      <Input
        label="Subject Line"
        value={formData.subject}
        onChange={(e) => setFormData({...formData, subject: e.target.value})}
        placeholder="Welcome to CertifiAble, {{firstName}}!"
        required
      />

      {/* Header Text */}
      <Input
        label="Email Header Text"
        value={formData.headerText}
        onChange={(e) => setFormData({...formData, headerText: e.target.value})}
        placeholder="Welcome to CertifiAble! 🤟"
        helpText="This appears in the blue gradient header at the top of the email"
        required
      />

      {/* Rich Text Editor */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Email Content</label>
        
        {/* Editor Styles */}
        <style>{`
          [contenteditable] ul {
            list-style-type: disc;
            margin-left: 20px;
            padding-left: 20px;
          }
          [contenteditable] ol {
            list-style-type: decimal;
            margin-left: 20px;
            padding-left: 20px;
          }
          [contenteditable] li {
            display: list-item;
          }
          [contenteditable] a {
            color: #3B82F6;
            text-decoration: underline;
          }
        `}</style>
        
        {/* Toolbar */}
        <div className="flex gap-1 p-2 bg-gray-100 border border-gray-300 rounded-t-md flex-wrap">
          <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => execCommand('bold')} className="p-2 hover:bg-gray-200 rounded" title="Bold">
            <Bold className="w-4 h-4" />
          </button>
          <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => execCommand('italic')} className="p-2 hover:bg-gray-200 rounded" title="Italic">
            <Italic className="w-4 h-4" />
          </button>
          <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => execCommand('underline')} className="p-2 hover:bg-gray-200 rounded" title="Underline">
            <Underline className="w-4 h-4" />
          </button>
          <div className="w-px bg-gray-400 mx-1"></div>
          <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => insertList(false)} className="p-2 hover:bg-gray-200 rounded" title="Bullet List">
            <List className="w-4 h-4" />
          </button>
          <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => insertList(true)} className="p-2 hover:bg-gray-200 rounded" title="Numbered List">
            <ListOrdered className="w-4 h-4" />
          </button>
          <div className="w-px bg-gray-400 mx-1"></div>
          <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={insertButton} className="px-3 py-1 hover:bg-gray-200 rounded text-sm font-medium" title="Insert Button">
            + Button
          </button>
          <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={insertLink} className="p-2 hover:bg-gray-200 rounded" title="Insert Link">
            <Link className="w-4 h-4" />
          </button>
        </div>
        
        {/* Editor */}
        <div
          ref={editorRef}
          contentEditable
          onInput={updatePreview}
          onBlur={updateContent}
          suppressContentEditableWarning
          className="min-h-[400px] p-4 border border-gray-300 border-t-0 rounded-b-md focus:outline-none focus:ring-2 focus:ring-primary bg-white"
          style={{ 
            fontSize: '14px', 
            lineHeight: '1.6',
            listStylePosition: 'inside'
          }}
        >
          <p>Start typing your email...</p>
        </div>
      </div>

      {/* Preview */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Email Preview (with header & footer)</label>
        <div 
          className="border border-gray-300 rounded-md p-4 bg-gray-50"
          dangerouslySetInnerHTML={{ __html: formData.bodyHtml }}
        />
      </div>
    </div>
  );
}
